import Stepper from '@/components/Stepper';
import ToolBar from '@/components/ToolBar';
import { zodResolver } from '@hookform/resolvers/zod';
import { useContext, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { FormProvider, useForm } from 'react-hook-form';
import { ClientInfo } from '@pages/clients/steppers/ClientInfo';
import ClientDataExchange from './steppers/ClientDataExchange';
import ClientNotes from './steppers/ClientNotes';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AlertBox from '@/components/AlertBox';
import { toast } from '@/components/ui/use-toast';
import {
  ClientContactSchema,
  ClientDataExchangeSchema,
  ClientInformationSchema,
  ClientInstructionSchema,
} from './client.schema';
import { ClientCreateRequest, ClientResponse, ContactResponse, useClientPost } from '@brainvaulttech/dgr-react-sdk';
import { removeNullValues } from '@/lib/utils';
import { getPhoneNumberValue } from '@/components/FormFieldPhone';
import { NavigationBlockerContext } from '@/context/NavigationBlockerContext';
import { displayError } from '@/lib/error';
import { ContactForm } from '@/components/ContactForm';
import { normalizeEmailList } from '@/utils/helper';

const schemas = [ClientInformationSchema, ClientContactSchema, ClientDataExchangeSchema, ClientInstructionSchema];

export const ClientCreate = ({
  displayType = 'PAGE',
  onSave,
  onCancel,
}: {
  displayType?: 'MODAL' | 'PAGE';
  onSave?: (client: ClientResponse) => void;
  onCancel?: () => void;
}) => {
  const [searchParam, setSearchParam] = useSearchParams();
  const [activeStep, setActiveStep] = useState(0);
  const [currentSchema, setCurrentSchema] = useState(schemas[activeStep]);
  const [alertBoxOpen, setAlertBoxOpen] = useState(false);
  const navigate = useNavigate();
  const form = useForm({
    resolver: zodResolver(currentSchema),
    mode: 'onBlur',
  });
  const {
    trigger,
    clearErrors,
    formState: { isDirty },
  } = form;

  const { setIsDirty } = useContext(NavigationBlockerContext);

  useEffect(() => {
    if (displayType === 'PAGE') setIsDirty(isDirty);
  }, [isDirty]);

  const [steps, setSteps] = useState([
    { title: 'Client Information', isCompleted: false },
    { title: 'Employees/ Contact', isCompleted: false },
    { title: 'Data Exchange Contacts', isCompleted: false },
    { title: 'Instructions', isCompleted: false },
  ]);

  useEffect(() => {
    if (displayType === 'PAGE') setSearchParam({ stepper: steps[0].title }, { replace: true });
  }, []);

  useEffect(() => {
    if (displayType === 'PAGE') {
      const activeStep = searchParam.get('stepper');
      if (activeStep) {
        const index = steps.findIndex((step) => step.title === activeStep);
        setActiveStep(index);
      }
    }
  }, [searchParam]);

  const scrollToTop = () => {
    const toolbarElement = document.getElementById('toolbar');
    if (toolbarElement) {
      toolbarElement.scrollIntoView({ block: 'end', behavior: 'smooth' });
    }
  };

  useEffect(() => {
    setCurrentSchema(schemas[activeStep]);
    setTimeout(async () => {
      await trigger();
      clearErrors();
    }, 50);
    setTimeout(() => {
      scrollToTop();
    }, 100);
  }, [activeStep]);

  const goToStep = async (step: number) => {
    const isValid = await trigger();
    if (isValid) {
      if (steps[step].isCompleted || step <= activeStep) {
        if (displayType === 'PAGE') setSearchParam({ stepper: steps[step].title });
        setActiveStep(step);
      }
    }
  };
  const nextStep = async () => {
    const isValid = await trigger();
    if (isValid) {
      setSteps((prevSteps) =>
        prevSteps.map((step, index) => (index === activeStep ? { ...step, isCompleted: true } : step))
      );
      if (displayType === 'PAGE') setSearchParam({ stepper: steps[activeStep + 1].title });
      setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
    }
  };

  const prevStep = () => {
    if (displayType === 'PAGE') setSearchParam({ stepper: steps[activeStep - 1].title });
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };
  const cancelHandler = () => {
    if (displayType === 'PAGE') {
      navigate('/clients');
      return;
    }
    onCancel?.();
  };
  const { mutate: createClient, status } = useClientPost();

  const saveHandler = () => {
    const values = form.getValues();
    if (values.type === 'FIRM') {
      delete values.firstName;
      delete values.lastName;
    }
    if (values.type === 'INDIVIDUAL') {
      delete values.clientName;
    }
    if (values.fax) values.fax = (values.fax as string).replace(/[-,()]/g, '');
    values.clientStatus = true;
    values.phone = getPhoneNumberValue(values.phone as string);
    values.arInvoicesCc = normalizeEmailList(values.arInvoicesCc);
    values.finalBillingSummaryCc = normalizeEmailList(values.finalBillingSummaryCc);
    values.recapAndInvoiceCc = normalizeEmailList(values.recapAndInvoiceCc);
    values.affidavitWithInvoiceCc =
      values?.canAffidavitWithInvoice && values?.affidavitWithInvoiceCc
        ? normalizeEmailList(values.affidavitWithInvoiceCc)
        : [];
    values.affidavitWithInvoice =
      values?.canAffidavitWithInvoice && values?.affidavitWithInvoice ? (values?.affidavitWithInvoice as string) : [];
    values.affidavitCc = normalizeEmailList(values.affidavitCc);
    values.statementCc = normalizeEmailList(values.statementCc);
    values.contacts = (values.contacts as ContactResponse[]).map((contact) => {
      return {
        ...contact,
        phone: getPhoneNumberValue(contact.phone),
        cell: getPhoneNumberValue(contact.cell),
        emailStatus: (contact.emailStatus = 'NONE'),
        ...(contact.fax && { fax: contact.fax.replace(/[-,()]/g, '') }),
      };
    });
    createClient(
      { data: removeNullValues(values) as ClientCreateRequest },
      {
        onSuccess: (data) => {
          displayType === 'PAGE' && navigate('/clients');
          onSave && onSave(data.data);
          toast({
            variant: 'default',
            description: `${data.data.clientName} has been added.`,
            duration: 3000,
          });
        },
        onError: (e) => {
          setAlertBoxOpen(false);
          displayError(e, 5000);
        },
      }
    );
  };

  return (
    <div className="w-full scrollbar-hide" id="stepper">
      {displayType === 'PAGE' && <ToolBar title="Add New Client" />}

      <div className="mt-3 flex justify-center pl-24">
        <Stepper steps={steps} activeStep={activeStep} goToStep={goToStep} />
      </div>

      <FormProvider {...form}>
        {activeStep === 0 && <ClientInfo />}
        {activeStep === 1 && <ContactForm mode="create" contactFor="CLIENT" />}
        {activeStep === 2 && <ClientDataExchange mode="create" goToStep={goToStep} />}
        {activeStep === 3 && <ClientNotes />}
      </FormProvider>

      <div className={`flex justify-end mt-4 mb-8 w-full gap-2`}>
        <Button
          variant="outline"
          className="text-primary py-2 px-4 rounded-md border-primary flex justify-center w-[125px] h-[48px] hover:bg-[#EDF5FF]"
          onClick={cancelHandler}
        >
          Cancel
        </Button>
        {activeStep !== 0 && (
          <Button
            variant="outline"
            className="text-primary  py-2 px-4 rounded-md border-primary  flex justify-center w-[125px] h-[48px] hover:bg-[#EDF5FF] "
            onClick={prevStep}
          >
            Back
          </Button>
        )}
        {activeStep !== steps.length - 1 && (
          <Button
            className="text-[#fff] py-2 px-4 rounded-md bg-primary  w-[125px] h-[48px] border-primaryflex justify-center  hover:bg-[#12375C] hover:text-white disabled:bg-primary "
            onClick={nextStep}
            disabled={activeStep === steps.length - 1 || !form.formState.isValid}
          >
            Next
          </Button>
        )}

        {activeStep === steps.length - 1 && (
          <AlertBox
            open={alertBoxOpen}
            triggerButton={
              <Button
                variant="outline"
                className="text-[#fff] py-2 px-4 rounded-md bg-primary w-[125px] h-[48px] border-primary flex justify-center hover:bg-[#12375C] hover:text-white "
                onClick={() => {
                  setAlertBoxOpen(true);
                }}
                disabled={!form.formState.isValid || status === 'pending'}
              >
                Save
              </Button>
            }
            title="Do you want to save the record?"
            description="This action will create a new client record in the database."
            cancelBtnLabel="Cancel"
            confirmBtnLabel="Save"
            cancelBtnClick={() => {
              setAlertBoxOpen(false);
            }}
            isLoading={status === 'pending'}
            confirmBtnClick={saveHandler}
          />
        )}
      </div>
    </div>
  );
};
